import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, CircleUser, X, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { dm, fmtDateTime, weekLabel } from "@/lib/format";
import { Button, Card, Field, Input, Loading, Page, PageHeader, StickyFooter, Switch } from "@/components/kit";

export default function ReportEmails() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["settings"], queryFn: () => api("/settings") });
  const [emails, setEmails] = useState([]);
  const [auto, setAuto] = useState(false);
  const [input, setInput] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { if (data) { setEmails(data.report_emails || []); setAuto(!!data.auto_send); } }, [data]);
  const dirty = data && (auto !== !!data.auto_send || JSON.stringify(emails) !== JSON.stringify(data.report_emails || []));
  const add = () => {
    const e = input.trim().toLowerCase();
    setErr("");
    if (!/^\S+@\S+\.\S+$/.test(e)) return setErr("E-mail inválido");
    if (emails.length >= 10) return setErr("Máximo de 10 e-mails");
    if (emails.includes(e)) return setErr("E-mail já adicionado");
    setEmails([...emails, e]);
    setInput("");
  };
  const save = useMutation({
    mutationFn: () => api("/settings/reports", { method: "PUT", body: { report_emails: emails, auto_send: auto } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); toast.success("Envio automático salvo"); },
    onError: (e) => toast.error(e.message),
  });
  const send = useMutation({
    mutationFn: async () => { if (dirty) await save.mutateAsync(); return api("/reports/send-now", { method: "POST" }); },
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["settings"] }); toast.success(`Relatório da semana ${dm(r.week_start)} enviado para ${r.count} e-mail(s)`); },
    onError: (e) => toast.error(e.message),
  });
  if (isLoading) return <Loading />;
  const last = data.last_report;
  return (
    <Page narrow>
      <PageHeader title="Envio automático" back />
      <div className="flex flex-col gap-4">
        <Card className="flex gap-3"><Mail size={18} style={{ color: "var(--brand)" }} className="shrink-0 mt-0.5" /><p className="text-sm">Toda segunda-feira às 8h (Brasília) o relatório da última conferência finalizada é enviado para os e-mails abaixo.</p></Card>
        <Card className="flex items-center gap-4">
          <div className="flex-1"><div className="h3">Enviar toda segunda-feira</div><div className="caption">{auto ? "Ativado" : "Desativado"}</div></div>
          <Switch on={auto} onChange={setAuto} data-testid="auto-send-switch" />
        </Card>
        <Card className="flex flex-col gap-4">
          <div className="flex gap-3 items-end">
            <div className="flex-1"><Field label="E-mail do sócio" error={err}><Input type="email" placeholder="socio@email.com" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="report-email-input" /></Field></div>
            <Button onClick={add} disabled={!input.trim()} data-testid="add-report-email-button">Adicionar</Button>
          </div>
          {!emails.length && <p className="caption">Nenhum e-mail cadastrado.</p>}
          {emails.map((e) => (
            <div key={e} className="flex items-center gap-3" data-testid={`report-email-${e}`}>
              <CircleUser size={18} className="muted" /><span className="flex-1 text-sm font-semibold">{e}</span>
              <button className="icon-btn" onClick={() => setEmails(emails.filter((x) => x !== e))} data-testid={`remove-report-email-${e}`}><X size={15} /></button>
            </div>
          ))}
        </Card>
        {last && (
          <Card className="card-soft" data-testid="last-report-card">
            <div className="label">Último envio ({last.mode === "auto" ? "automático" : "manual"})</div>
            <div className="caption">{weekLabel(last)} · {last.count} e-mail(s) · {fmtDateTime(last.sent_at)}</div>
          </Card>
        )}
        <Button variant="secondary" icon={Send} loading={send.isPending} disabled={!emails.length} onClick={() => send.mutate()} data-testid="send-report-now-button">Enviar relatório agora</Button>
      </div>
      <StickyFooter><Button disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()} data-testid="save-report-settings-button">{dirty ? "Salvar" : "Salvo"}</Button></StickyFooter>
    </Page>
  );
}
